/**
 * Type-Safe Settings System
 * Provides typed access to Shopify section/block settings
 */

export interface BaseSettings {
  [key: string]: string | number | boolean | undefined;
}

export interface VariantSelectorSettings extends BaseSettings {
  enable_url_sync: boolean;
  enable_notify_me: boolean;
  notify_endpoint?: string;
  show_inventory_count: boolean;
  low_inventory_threshold: number;
}

export interface CartDrawerSettings extends BaseSettings {
  enable_gift_threshold: boolean;
  gift_threshold_amount: number;
  gift_product_handle?: string;
  enable_free_shipping_bar: boolean;
  free_shipping_threshold: number;
  enable_bundle_hints: boolean;
  bundle_rules?: string; // JSON string of bundle rules
}

export interface RecommendationsSettings extends BaseSettings {
  source_type: 'complete_set' | 'recently_viewed' | 'trending';
  max_products: number;
  product_handles?: string; // Comma-separated handles
  metafield_namespace?: string;
  metafield_key?: string;
  heading: string;
}

/**
 * Parse section settings from DOM
 */
export class SettingsParser<T extends BaseSettings> {
  constructor(private element: HTMLElement) {}

  /**
   * Get a setting value with type safety
   */
  get<K extends keyof T>(key: K, defaultValue?: T[K]): T[K] | undefined {
    const attr = `data-${this.kebabCase(String(key))}`;
    const value = this.element.getAttribute(attr);

    if (value === null) {
      return defaultValue;
    }

    // Type coercion based on default value type
    if (typeof defaultValue === 'boolean') {
      return (value === 'true') as T[K];
    }

    if (typeof defaultValue === 'number') {
      return Number(value) as T[K];
    }

    return value as T[K];
  }

  /**
   * Get all settings as object
   */
  getAll(): Partial<T> {
    const settings: Partial<T> = {};
    const attrs = this.element.attributes;

    for (let i = 0; i < attrs.length; i++) {
      const attr = attrs[i];
      if (attr.name.startsWith('data-')) {
        const key = this.camelCase(attr.name.replace('data-', '')) as keyof T;
        settings[key] = this.parseValue(attr.value) as T[keyof T];
      }
    }

    return settings;
  }

  /**
   * Parse JSON setting
   */
  getJSON<R = unknown>(key: keyof T): R | null {
    const value = this.get(key);
    if (!value || typeof value !== 'string') {
      return null;
    }

    try {
      return JSON.parse(value) as R;
    } catch {
      console.error(`Failed to parse JSON for setting: ${String(key)}`);
      return null;
    }
  }

  private parseValue(value: string): string | number | boolean {
    if (value === 'true') return true;
    if (value === 'false') return false;
    if (!isNaN(Number(value)) && value !== '') return Number(value);
    return value;
  }

  private kebabCase(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  private camelCase(str: string): string {
    return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
  }
}
